import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import ControlledTextField from "../../../components/ControlledTextField";
import { Form, Button } from "react-bootstrap";
import { CardText, CardTitle, Container } from "reactstrap";
import { toast } from "react-toastify";
import logo from "../../../assets/icons/logo1.PNG";
import GoogleIcon from "../../../assets/icons/google-icon.PNG";
import "firebase/auth";
import "./index.css";
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail, // 🔥 Add this import
} from "firebase/auth";
import { auth, generateToken } from "../../../firebase";
import { trackLogin, trackSignUp, trackEvent } from "../../../utility/analyticsUtility";
import { useLazyGetExistingUserInfoQuery } from "../../../redux/services/getUserService";
import GoogleSignUpData from "../SignUp/GoogleSignUpData";
import { useCreateCustomerMutation } from "../../../redux/services/customerCreateService";
import { useLazyGetCustomerProfileQuery } from "../../../redux/services/getCustomer";
import { useAddMessageTokenMutation } from "../../../redux/services/userService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowLeft } from "@fortawesome/free-solid-svg-icons"; // 🔥 Add faArrowLeft
import { useDispatch } from "react-redux";
import { setCustomer } from "../../../redux/customerSlice";
import { validateProfilePhotoUrl } from "../../../utility/Utility";
import Swal from "sweetalert2"; // 🔥 Add this import

type FormData = {
  email: string;
  password: string;
};

interface SignInModalProps {
  setModalOpen: any;
  setIsSignInModalOpen: any;
}

const SignInModal: React.FC<SignInModalProps> = ({
  setModalOpen,
  setIsSignInModalOpen,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // 🔥 Add forgot password state
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [dots, setDots] = useState("");

  const [userData, setUserData] = useState<any>({
    displayName: "",
    email: "",
    phoneNumber: "",
    photoUrl: "",
  });

  const [isDataModalOpen, setIsDataModalOpen] = useState<boolean>(false);

  const [triggerGetUserInfo] = useLazyGetExistingUserInfoQuery();
  const [addMessageToken] = useAddMessageTokenMutation();
  const [createCustomer] = useCreateCustomerMutation();

  // 🔥 Update form with validation mode
  const { control, handleSubmit, formState: { errors, isValid }, getValues, reset } = useForm<FormData>({
    mode: 'onChange', // Enable real-time validation
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const [triggerGetCustomerInfo] = useLazyGetCustomerProfileQuery();

  // 🔥 Toggle to forgot password mode
  const handleForgotPasswordMode = () => {
    setIsForgotPasswordMode(true);
    reset({ email: getValues("email"), password: "" }); // Keep email, clear password
  };

  // 🔥 Cancel forgot password mode
  const handleCancelForgotPassword = () => {
    setIsForgotPasswordMode(false);
    reset({ email: "", password: "" }); // Clear form
  };

  // 🔥 Forgot Password Handler
  const handleForgotPassword = async (data: FormData) => {
    setIsResettingPassword(true);
    try {
      await sendPasswordResetEmail(auth, data.email, {
        url: `${window.location.origin}`, // Redirect back to home after reset
        handleCodeInApp: false,
      });

      setIsResettingPassword(false);

      // Analytics: password reset requested
      try {
        trackEvent({
          event: 'password_reset_requested',
          category: 'Authentication',
          action: 'Forgot Password',
          label: data.email,
        });
      } catch (e) {
        // ignore analytics errors
      }

      Swal.fire({
        title: "Password Reset Email Sent! 📧",
        html: `
          <div style="text-align: left; padding: 10px;">
            <p>We've sent a password reset link to:</p>
            <p><strong style="color: #590b21;">${data.email}</strong></p>
            <hr style="margin: 15px 0;">
            <p><strong>Next Steps:</strong></p>
            <ol style="text-align: left; padding-left: 20px;">
              <li>Check your email inbox</li>
              <li>Look in spam/junk folder if not found</li>
              <li>Click the reset link in the email</li>
              <li>Create your new password</li>
            </ol>
            <small class="text-muted">If you don't receive the email within 5 minutes, please try again.</small>
          </div>
        `,
        icon: "success",
        confirmButtonColor: "#590b21",
        confirmButtonText: "Got it!",
        width: '500px'
      }).then(() => {
        setIsForgotPasswordMode(false);
        reset({ email: "", password: "" });
      });

    } catch (error: any) {
      setIsResettingPassword(false);
      console.error("Password reset error:", error);

      let errorMessage = "Failed to send password reset email. Please try again.";

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = "No account found with this email address. Please check your email or sign up for a new account.";
          break;
        case 'auth/invalid-email':
          errorMessage = "Please enter a valid email address.";
          break;
        case 'auth/too-many-requests':
          errorMessage = "Too many reset requests. Please wait a few minutes before trying again.";
          break;
        default:
          errorMessage = error.message || "An error occurred while sending the password reset email.";
      }

      toast.error(errorMessage);
    }
  };

  const GetCustomerData = async (accessToken: string) => {
    const response = await triggerGetCustomerInfo(accessToken);
    if (response.data?.id) {
      localStorage.setItem("customerId", response.data.id);
    }
    sendMessageToken();
  };

  const sendMessageToken = async () => {
    const token = await generateToken();
    if (token) {
      const newCustomerId = localStorage.getItem("customerId") || "";
      if (newCustomerId !== "") {
        await addMessageToken({
          token,
          userType: "WebCustomer",
          customerId: newCustomerId,
        });
      }
    }
  };

  const handleSignIn = async (data: FormData) => {
    const { email, password } = data;
    if (!email || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        setIsLoading(false);
        
        Swal.fire({
          title: "Email Not Verified ⚠️",
          html: `
            <div style="text-align: left; padding: 10px;">
              <p>Your email address has not been verified yet.</p>
              <p style="color: #590b21;"><strong>Email:</strong> ${email}</p>
              <hr style="margin: 15px 0;">
              <p><strong>To continue:</strong></p>
              <ol style="text-align: left; padding-left: 20px;">
                <li>Check your email inbox for the verification link</li>
                <li>Click the verification link in the email</li>
                <li>Return here and sign in again</li>
              </ol>
              <p style="margin-top: 15px;"><small class="text-muted">Didn't receive the email? Check your spam folder or request a new verification email.</small></p>
            </div>
          `,
          icon: "warning",
          confirmButtonColor: "#590b21",
          confirmButtonText: "OK",
          showCancelButton: true,
          cancelButtonText: "Resend Verification Email",
          cancelButtonColor: "#6c757d",
          width: '550px'
        }).then(async (result) => {
          if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
            // User clicked "Resend Verification Email"
            try {
              const { sendEmailVerification } = await import('firebase/auth');
              await sendEmailVerification(userCredential.user);
              toast.success("Verification email resent! Please check your inbox.");
            } catch (resendError: any) {
              toast.error("Failed to resend verification email. Please try again later.");
              console.error("Resend verification error:", resendError);
            }
          }
        });

        // Sign out the user
        await auth.signOut();
        return;
      }

      const accessToken = await userCredential.user.getIdToken();

      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("isUserSignIN", "true");
      localStorage.setItem("email", userCredential.user.email || "");
      localStorage.setItem("photoURL", userCredential.user.photoURL || "");
      localStorage.setItem("ownerID", userCredential.user.uid);

      const response = await triggerGetUserInfo(accessToken);
      if (response?.data?.userRoles?.includes("customer")) {
        toast.dismiss();
        toast.success("Welcome Back! You've Logged In Successfully!");

        // Analytics: successful email login
        try {
          trackLogin('email', response.data.userId);
        } catch (e) {}

        dispatch(setCustomer({
          id: response.data.userId,
          userId: response.data.userId,
          displayName: response.data.displayName || '',
          email: response.data.email || '',
          phoneNumber: response.data.phoneNumber || '',
          isAnonymousUser: false,
          photoUrl: validateProfilePhotoUrl(response.data.photoUrl)
        }));
      } else {
        const userDataObj: any = {
          userId: response?.data?.userId,
          displayName: response?.data?.displayName,
          email: response?.data?.email,
          phoneNumber: response?.data?.phoneNumber,
          photoUrl: response?.data?.photoUrl
        };

        await createCustomer(userDataObj).unwrap();
        toast.dismiss();
        toast.success("Welcome ! You've Logged In As Customer Successfully!");

        // Analytics: new customer created via email sign-in flow
        try {
          trackSignUp('email', userCredential.user.uid);
        } catch (e) {}

        dispatch(setCustomer({
          id: response?.data?.userId,
          userId: response?.data?.userId,
          displayName: response?.data?.displayName || '',
          email: response?.data?.email || '',
          phoneNumber: response?.data?.phoneNumber || '',
          isAnonymousUser: false,
          photoUrl: userCredential.user.photoURL,
          settings: {
            requiresOrderVerification: false
          },
          addresses: []
        }));
      }
      GetCustomerData(accessToken);
      setModalOpen(false);
      try {
        const returnTo = sessionStorage.getItem('auth_return_to');
        if (returnTo) {
          sessionStorage.removeItem('auth_return_to');
          navigate(returnTo);
        } else {
          navigate("/");
        }
      } catch (e) {
        navigate("/");
      }

    } catch (error: any) {
      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/user-not-found" ||
        error.code === "auth/invalid-credential"
      ) {
        toast.error("Invalid email or password. Please check your credentials.");
      } else if (error.code === "auth/too-many-requests") {
        toast.error("Too many failed attempts. Please try again later or reset your password.");
      } else {
        toast.error(error.message || "Invalid email or password.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    try {
      const userCredential = await signInWithPopup(auth, provider);
      
      // Note: Google accounts are pre-verified by Google, so no email verification check needed
      toast.success("Google Sign-In Successful!");
      const accessToken = await userCredential.user.getIdToken();
      localStorage.setItem("accessToken", accessToken);

      if (userCredential.user.uid) {
        localStorage.setItem("ownerID", userCredential.user.uid);
      }
      if (userCredential.user.email) {
        localStorage.setItem("email", userCredential.user.email);
      }
      if (userCredential.user.photoURL) {
        localStorage.setItem("photoURL", userCredential.user.photoURL);
      }

      const response = await triggerGetUserInfo(accessToken);

      if (response?.data?.userRoles?.includes("customer")) {
        toast.success("Welcome back!");
        dispatch(setCustomer({
          id: response.data.userId,
          userId: response.data.userId,
          displayName: response.data.displayName || userCredential.user.displayName || '',
          email: response.data.email || userCredential.user.email || '',
          phoneNumber: response.data.phoneNumber || '',
          isAnonymousUser: false,
          photoUrl: userCredential.user.photoURL
        }));
        setModalOpen(false);
        GetCustomerData(accessToken);
        // Analytics: google login
        try {
          trackLogin('google', response.data.userId);
        } catch (e) {}
      } else {
        toast.info("New user detected, please complete signup.");
        setIsDataModalOpen(true);
        const userDataObj: any = {
          userId: userCredential.user.uid,
          displayName: userCredential.user.displayName || "New User",
          email: userCredential.user.email,
          phoneNumber: userCredential.user.phoneNumber,
          photoUrl: userCredential.user.photoURL,
        };

        setUserData(userDataObj);
        setIsDataModalOpen(true);
        dispatch(setCustomer({
          id: userCredential.user.uid,
          userId: userCredential.user.uid,
          displayName: userCredential.user.displayName || "New User",
          email: userCredential.user.email || '',
          phoneNumber: userCredential.user.phoneNumber || '',
          isAnonymousUser: false,
          photoUrl: userCredential.user.photoURL,
          settings: {
            requiresOrderVerification: false
          },
          addresses: []
        }));
        // Analytics: new user via Google sign-in
        try {
          trackSignUp('google', userCredential.user.uid);
        } catch (e) {}
      }

      localStorage.setItem("isUserSignIN", "true");
      try {
        const returnTo = sessionStorage.getItem('auth_return_to');
        if (returnTo) {
          sessionStorage.removeItem('auth_return_to');
          navigate(returnTo);
        } else {
          navigate("/");
        }
      } catch (e) {
        navigate("/");
      }

    } catch (error: any) {
      console.error("Error signing in with Google: ", error);
      toast.error(error.message || "Failed to sign in with Google.");
    }
  };

  // 🔥 Form submit handler - handles both sign in and forgot password
  const onSubmit = (data: FormData) => {
    if (isForgotPasswordMode) {
      handleForgotPassword(data);
    } else {
      handleSignIn(data);
    }
  };

  // 🔥 Loading dots animation
  useEffect(() => {
    if (!isLoading && !isResettingPassword) return;

    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + "." : ""));
    }, 500);
    return () => clearInterval(interval);
  }, [isLoading, isResettingPassword]);

  return (
    <div>
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
      >
        <div
          className="p-4 card-signin text-center"
          style={{ maxWidth: "450px" }}
        >
          <div className="mb-1">
            <img
              src={logo}
              alt="loginimage"
              style={{ width: "200px", marginRight: "10px" }}
            />
          </div>
          {isDataModalOpen ? (
            <GoogleSignUpData
              userData={userData}
              setUserData={setUserData}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              setIsDataModalOpen={setIsDataModalOpen}
              setModalOpen={setModalOpen}
            />
          ) : (
            <>
              {/* 🔥 Dynamic Title and Description */}
              <CardTitle tag="h2" className="fw-bold text-light mb-1">
                {isForgotPasswordMode
                  ? "Reset Your Password 🔐"
                  : "Welcome to Curry King !"}
              </CardTitle>
              <CardText className="mb-1 text-light">
                {isForgotPasswordMode
                  ? "Enter your email address and we'll send you a link to reset your password."
                  : "Please sign in to your account and start the adventure."}
              </CardText>

              <div className="auth-login-form mt-1">
                <Form onSubmit={handleSubmit(onSubmit)}>
                  {/* 🔥 Email field with validation */}
                  <div className="mb-3">
                    <ControlledTextField
                      label={""}
                      inputName={"email"}
                      inputType={"email"}
                      placeholder={"Enter Your Email"}
                      isRequired={true}
                      control={control}
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Please enter a valid email address"
                        }
                      }}
                    />
                    {errors.email && (
                      <div className="text-danger mt-1 small text-start">
                        {errors.email.message}
                      </div>
                    )}
                  </div>

                  {/* 🔥 Password field - only show in sign-in mode */}
                  {!isForgotPasswordMode && (
                    <div className="mb-3">
                      <ControlledTextField
                        label={""}
                        inputName="password"
                        inputType="password"
                        placeholder="Enter Password"
                        isRequired={true}
                        control={control}
                        rules={{
                          required: "Password is required",
                          minLength: {
                            value: 6,
                            message: "Password must be at least 6 characters"
                          }
                        }}
                      />
                      {errors.password && (
                        <div className="text-danger mt-1 small text-start">
                          {errors.password.message}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 🔥 Buttons Section */}
                  {isForgotPasswordMode ? (
                    // Forgot Password Mode Buttons
                    <div className="d-flex gap-2 mb-3">
                      <Button
                        variant="outline-light"
                        onClick={handleCancelForgotPassword}
                        disabled={isResettingPassword}
                        className="flex-fill"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back
                      </Button>
                      <button
                        type="submit"
                        className="btn btn-signin-color flex-fill"
                        disabled={!isValid || isResettingPassword}
                      >
                        {isResettingPassword ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Sending{dots}
                          </>
                        ) : (
                          "Send Reset Link"
                        )}
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* <div className="d-grid">
                        <button
                          type="submit"
                          className="btn my-2 py-2 btn-signin-color btn-block"
                          disabled={isLoading || !isValid}
                        >
                          {isLoading ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                              Signing In{dots}
                            </>
                          ) : (
                            "Sign In"
                          )}
                        </button>
                      </div> */}
                      <div className="d-grid">
                        <button
                          type="submit"
                          className="btn my-2 py-2 btn-signin-color btn-block position-relative"
                          disabled={isLoading || !isValid}
                        >
                          {isLoading ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                              Signing In
                              <span className="dots-container">{dots}</span>
                            </>
                          ) : (
                            "Sign In"
                          )}
                        </button>
                      </div>

                      {/* 🔥 Forgot Password Link */}
                      <div className="text-center text-light mt-2 mb-3">
                        <button
                          type="button"
                          className="btn btn-link text-light p-0 text-decoration-none"
                          style={{ fontSize: "14px" }}
                          onClick={handleForgotPasswordMode}
                        >
                          Forgot Password?
                        </button>
                      </div>

                      {/* Sign Up Link */}
                      <div className="text-center text-light mt-3">
                        <p>
                          No account? Please{" "}
                          <div
                            className="btn mx-2 text-link-color"
                            onClick={() => {
                              setIsSignInModalOpen(false);
                            }}
                          >
                            SignUp
                          </div>
                        </p>
                      </div>
                    </>
                  )}
                </Form>

                {/* 🔥 Google Sign In - only show in normal mode */}
                {!isForgotPasswordMode && (
                  <div className="d-grid mt-3">
                    <button
                      className="btn text-link-color d-flex align-items-center justify-content-center"
                      onClick={handleGoogleSignIn}
                    >
                      <img
                        src={GoogleIcon}
                        alt="Google Icon"
                        style={{ width: "20px", marginRight: "10px" }}
                      />
                      Sign In with Google
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default SignInModal;
